import { FormGroup, TextInput } from '@allurion/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { TrackedButton } from 'src/analytics/TrackedUI';
import { PasswordPopup } from 'src/components/PasswordPopup/PasswordPopup';
import globalMessages from 'src/messages/global.messages';

import messages from './validate-email-code-messages';

import styles from './ValidateEmailCodeForm.module.scss';

type FormValues = {
  code: string;
  password: string;
};

type Props = {
  onSubmit: (values: FormValues) => void;
  resendEmail: () => void;
};

export function ValidateEmailCodeForm({ onSubmit, resendEmail }: Props) {
  const intl = useIntl();

  const ValidateEmailCodeFormSchema = z.object({
    code: z.string().nonempty(intl.formatMessage(globalMessages.required)),
    password: z
      .string()
      .nonempty(intl.formatMessage(globalMessages.required))
      .min(8, intl.formatMessage(globalMessages.minChars))
      .regex(/[a-z]/, intl.formatMessage(globalMessages.oneLower))
      .regex(/[A-Z]/, intl.formatMessage(globalMessages.oneCapital))
      .regex(/[0-9]/, intl.formatMessage(globalMessages.oneNumber))
      .regex(/[!@#$%^&*()_+{}:;'"/\\]/, intl.formatMessage(globalMessages.oneSpecialChar)),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(ValidateEmailCodeFormSchema),
  });

  return (
    <>
      <p className={styles.text}>
        {intl.formatMessage(globalMessages.resetPasswordVerificationCodeText)}
      </p>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <FormGroup
          label={intl.formatMessage(messages.yourVerficationCode)}
          error={errors.code?.message}
        >
          <TextInput
            type="text"
            placeholder={intl.formatMessage(messages.verficationCode)}
            autoComplete="off"
            {...register('code')}
          />
        </FormGroup>
        <FormGroup
          label={
            <span style={{ display: 'flex', gap: '10px' }}>
              {`${intl.formatMessage(messages.newPassword)} `}
              <PasswordPopup />
            </span>
          }
          error={errors.password?.message}
        >
          <TextInput
            type="password"
            placeholder={intl.formatMessage(messages.password)}
            autoComplete="new-password"
            {...register('password')}
          />
        </FormGroup>

        <div className={styles.controls}>
          <TrackedButton
            type="submit"
            label={intl.formatMessage(messages.submit)}
            variant="secondary"
            trackLabel="submit-validate-email-code"
          />
          <Link
            className={styles['styled-link']}
            to="#!"
            onClick={(evt: any) => {
              evt.preventDefault();
              resendEmail();
            }}
          >
            {intl.formatMessage(messages.resendVerfification)}
          </Link>
          <div className={styles['email-wrapper']}>
            {`${intl.formatMessage(messages.email)} `}
            <a
              className={styles['styled-link']}
              href="mailto:help@allurion.com"
              target="_blank"
              rel="noreferrer"
            >
              help@allurion.com
            </a>
          </div>
        </div>
      </form>
    </>
  );
}
